/**
 * ObjectiveHeader Component
 * 
 * Displays objective details including name, description, metrics, timeline, and progress.
 */

import React from 'react';
import { Card, Progress, Tag, Statistic, Row, Col, Tooltip, Typography } from 'antd';
import { 
  TrophyOutlined, 
  CalendarOutlined, 
  AimOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  SyncOutlined,
  PauseCircleOutlined,
} from '@ant-design/icons';
import { ObjectiveWithDetails, ObjectiveMetrics, ObjectiveTimeline } from '../../../types/objective';
import styles from './ObjectiveHeader.module.css';

const { Title, Text, Paragraph } = Typography;

interface ObjectiveHeaderProps {
  objective: ObjectiveWithDetails;
  className?: string;
}

// Status configuration
const statusConfig: Record<string, { color: string; icon: React.ReactNode; label: string }> = {
  not_started: { color: 'default', icon: <PauseCircleOutlined />, label: 'Not Started' },
  in_progress: { color: 'processing', icon: <SyncOutlined spin />, label: 'In Progress' },
  completed: { color: 'success', icon: <CheckCircleOutlined />, label: 'Completed' },
  cancelled: { color: 'error', icon: <ClockCircleOutlined />, label: 'Cancelled' },
};

// Format date for display
function formatDate(dateStr?: string): string {
  if (!dateStr) return 'Not set';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric', 
    year: 'numeric' 
  });
}

// Calculate days remaining/overdue
function calculateDaysRemaining(targetDate?: string): { days: number; status: 'remaining' | 'overdue' | 'today' } | null {
  if (!targetDate) return null;
  
  const target = new Date(targetDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);
  
  const diffTime = target.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return { days: 0, status: 'today' };
  if (diffDays > 0) return { days: diffDays, status: 'remaining' };
  return { days: Math.abs(diffDays), status: 'overdue' };
}

// Render metrics section
const MetricsSection: React.FC<{ metrics: ObjectiveMetrics }> = ({ metrics }) => {
  if (!metrics || Object.keys(metrics).length === 0) {
    return <Text type="secondary">No target metrics defined</Text>;
  }

  const targetValue = metrics.target_value ?? 0;
  const currentValue = metrics.current_value ?? 0;
  const unit = metrics.unit || '';
  const progressPercent = targetValue > 0 ? Math.min((currentValue / targetValue) * 100, 100) : 0;

  return (
    <div className={styles.metricsSection}>
      <div className={styles.metricProgress}>
        <div className={styles.metricValues}>
          <Statistic 
            title="Current" 
            value={currentValue} 
            suffix={unit === 'percent' ? '%' : unit}
            valueStyle={{ color: '#3f8600' }}
          />
          <Text className={styles.metricDivider}>/</Text>
          <Statistic 
            title="Target" 
            value={targetValue} 
            suffix={unit === 'percent' ? '%' : unit}
          />
        </div>
        <Progress 
          percent={Math.round(progressPercent)} 
          status={progressPercent >= 100 ? 'success' : 'active'}
          strokeColor={{
            '0%': '#108ee9',
            '100%': '#87d068',
          }}
        />
      </div>
      {metrics.target_type && (
        <Tag color="blue" className={styles.metricType}>
          {metrics.target_type.replace(/_/g, ' ')}
        </Tag>
      )}
    </div>
  );
};

// Render timeline section
const TimelineSection: React.FC<{ timeline: ObjectiveTimeline; status: string }> = ({ timeline, status }) => {
  const daysInfo = calculateDaysRemaining(timeline.target_completion);
  
  return (
    <div className={styles.timelineSection}>
      <Row gutter={[16, 16]}>
        <Col span={8}>
          <div className={styles.timelineItem}>
            <CalendarOutlined className={styles.timelineIcon} />
            <div>
              <Text type="secondary">Start Date</Text>
              <div className={styles.timelineValue}>{formatDate(timeline.start_date)}</div>
            </div>
          </div>
        </Col>
        <Col span={8}>
          <div className={styles.timelineItem}>
            <AimOutlined className={styles.timelineIcon} />
            <div>
              <Text type="secondary">Target Completion</Text>
              <div className={styles.timelineValue}>{formatDate(timeline.target_completion)}</div>
            </div>
          </div>
        </Col>
        <Col span={8}>
          <div className={styles.timelineItem}>
            <ClockCircleOutlined className={styles.timelineIcon} />
            <div>
              <Text type="secondary">
                {status === 'completed' ? 'Completed On' : 'Time Remaining'}
              </Text>
              <div className={styles.timelineValue}>
                {status === 'completed' ? (
                  formatDate(timeline.actual_completion)
                ) : daysInfo ? (
                  <span className={styles[daysInfo.status]}>
                    {daysInfo.status === 'today' ? 'Due Today' : 
                     daysInfo.status === 'remaining' ? `${daysInfo.days} days remaining` :
                     `${daysInfo.days} days overdue`}
                  </span>
                ) : (
                  'No deadline'
                )}
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export const ObjectiveHeader: React.FC<ObjectiveHeaderProps> = ({ objective, className }) => {
  const status = statusConfig[objective.status] || statusConfig.not_started;
  
  return (
    <Card 
      className={`${styles.objectiveHeader} ${className || ''}`}
      bordered={false}
    >
      <div className={styles.headerTop}>
        <div className={styles.titleSection}>
          <div className={styles.titleRow}>
            <TrophyOutlined className={styles.titleIcon} />
            <Title level={3} className={styles.title}>
              {objective.objective.name}
            </Title>
            <Tag 
              color={status.color} 
              icon={status.icon}
              className={styles.statusTag}
            >
              {status.label}
            </Tag>
          </div>
          {objective.objective.description && (
            <Paragraph className={styles.description}>
              {objective.objective.description}
            </Paragraph>
          )}
        </div>
        
        <div className={styles.progressSection}>
          <Tooltip title={`${objective.completion_percentage}% complete`}>
            <Progress 
              type="circle" 
              percent={objective.completion_percentage} 
              size={100}
              strokeColor={{
                '0%': '#108ee9',
                '100%': '#87d068',
              }}
              format={(percent) => (
                <div className={styles.progressCircleContent}>
                  <span className={styles.progressPercent}>{percent}%</span>
                  <span className={styles.progressLabel}>Complete</span>
                </div>
              )}
            />
          </Tooltip>
        </div>
      </div>

      <div className={styles.headerBody}>
        <div className={styles.section}>
          <Title level={5} className={styles.sectionTitle}>
            <AimOutlined /> Target Metrics
          </Title>
          <MetricsSection metrics={objective.metrics} />
        </div>

        <div className={styles.section}>
          <Title level={5} className={styles.sectionTitle}>
            <CalendarOutlined /> Timeline
          </Title>
          <TimelineSection timeline={objective.timeline} status={objective.status} />
        </div>
      </div>
    </Card>
  );
};

export default ObjectiveHeader;
